<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">Dashboard</h4>
        <div>
          <b-button variant="outline-secondary" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
        </div>
      </div>

      <div class="py-5 text-center" v-if="isLoadingOverviewData">
        <b-spinner />
      </div>
      <b-row v-if="!isLoadingOverviewData && overviewData" class="mb-5">
        <b-col v-if="isCustomer()" cols="6" lg="3" class="p-2">
          <div class="text-center rounded py-3" :style="{ 'background-color': '#D7F0F7' }">
            <div><label>{{ $t('subPlanName') }}</label></div>
            <div><strong style="font-size: 1.25rem">{{ activeSubscriptionPlan.name }}</strong></div>
          </div>
        </b-col>
        <b-col v-if="isCustomer()" cols="6" lg="3" class="p-2">
          <div class="text-center rounded py-3" :style="{ 'background-color': '#D7F0F7' }">
            <div><label>{{ $t('expDate') }}</label></div>
            <div><strong style="font-size: 1.25rem">{{ localDateFormat(activeCustomerSubscription.endTime) }}</strong></div>
          </div>
        </b-col>
        <b-col v-for="overviewItem in overviewItems" :key="overviewItem.key" cols="6" lg="3" class="p-2">
          <div class="text-center rounded py-3" :style="{ 'background-color': overviewItem.bgColor }">
            <div><label>{{ overviewItem.text }}</label></div>
            <div><strong style="font-size: 1.25rem">{{ overviewItem.value }}</strong></div>
          </div>
        </b-col>
      </b-row>

      <div class="py-5 text-center" v-if="isLoadingMeetingParticipantsCountData">
        <b-spinner />
      </div>
      <template v-if="!isLoadingMeetingParticipantsCountData && meetingParticipantsCountData">
        <div class="border pt-4 pb-2 mb-5 d-flex flex-column align-items-center" style="height: 600px;">
          <h5 class="mb-3">{{ $t("dashboardChartTitle") }}</h5>
          <b-form class="d-flex flex-row align-items-center" style="width: 600px;" @submit.prevent="onClickApplyChartDates">
            <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
              <div class="flex-shrink-0 mr-3">{{ $t("fromDate") }}</div>
              <b-form-datepicker v-model="chartFilterStartDate" value-as-date required
                                 :max="new Date()"
                                 no-flip hide-header size="sm"
                                 label-no-date-selected="DD/MM/YYYY"
                                 locale="vi"
                                 :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                                 start-weekday="1"></b-form-datepicker>
            </div>
            <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
              <div class="flex-shrink-0 mr-3">{{ $t("toDate") }}</div>
              <b-form-datepicker v-model="chartFilterEndDate" value-as-date required
                                 :max="new Date()"
                                 no-flip hide-header size="sm"
                                 label-no-date-selected="DD/MM/YYYY"
                                 locale="vi"
                                 :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                                 start-weekday="1"></b-form-datepicker>
            </div>
            <div>
              <b-button type="submit" variant="primary" size="sm">{{ $t("apply") }}</b-button>
            </div>
          </b-form>
          <div class="text-danger">{{ chartFilterFormErrorMsg }}</div>
          <v-chart class="chart" :option="chartOptions"/>
        </div>
      </template>

      <h4 class="mb-4">{{ $t("meetingsListToday") }} <span v-if="todayText">({{ todayText }})</span></h4>
      <b-table ref="table" :items="meetingsToday" :busy="isLoadingMeetingsToday" hover show-empty primary-key="id" :fields="tableFields">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>

        <template #cell(name)="data">
          <router-link v-if="data.item.owner" :to="`/meetings/details/${data.item.id}`">{{ data.item.name }}</router-link>
          <span v-else>{{ data.item.name }}</span>
          <br>
          <span class="mr-3" v-if="data.item.enableCamera" v-b-tooltip :title="$t('meetingShareCamera')">
            <b-icon-camera-video />
          </span>
          <span class="mr-3" v-else v-b-tooltip :title="$t('meetingTurnOffCamera')">
            <b-icon-camera-video-off />
          </span>
          <template v-if="isBBB(data.item)">
            <span class="mr-3" v-if="data.item.enableMuteOnStart" v-b-tooltip :title="$t('meetingMuteOnStart')">
              <b-icon-mic-mute />
            </span>
            <span class="mr-3" v-if="data.item.enablePrivateChat || data.item.enablePublicChat" v-b-tooltip
                  :title="getMeetingChatSettingsTooltipText(data.item)">
              <b-icon-chat-dots />
            </span>
            <span class="mr-3" v-else v-b-tooltip :title="$t('meetingChatNotAllow')">
              <b-iconstack>
                <b-icon-chat />
                <b-icon-slash rotate="90" font-scale="2" shift-h="-4" shift-v="4" />
              </b-iconstack>
            </span>
          </template>
        </template>

        <template v-slot:cell(datetime)="data">
          <template v-if="data.item.actualStartTime && data.item.actualEndTime">
            {{ localTimeFormat(data.item.actualStartTime) }} - {{ localTimeFormat(data.item.actualEndTime) }}
          </template>
          <template v-else>{{ localTimeFormat(data.item.scheduleStartTime) }} - {{ localTimeFormat(data.item.scheduleEndTime) }}</template>
        </template>

        <template v-slot:cell(status)="data">
          <b-badge :variant="meetingStatusBadgeVariant[data.item.status]">{{ meetingStatusText[data.item.status] }}</b-badge>
        </template>

        <template v-slot:cell(actions)="data">
          <b-button v-if="isUserAdmin()" variant="link" size="sm" :to="`/monitor-meeting/${data.item.id}`"
                    v-b-tooltip.hover :title="$t('viewStats')">
            <b-icon-graph-up></b-icon-graph-up>
          </b-button>
        </template>
      </b-table>

    </b-container>
  </main>
</template>

<style lang="scss" scoped>
</style>

<script lang="ts">
  import {Component, Vue, Watch} from 'vue-property-decorator';
  import _ from "lodash";
  import moment from "moment";
  import BaseComponent from "@/components/BaseComponent";
  import * as reportService from "@/service/reportService";
  import {BvTableFieldArray} from "bootstrap-vue";
  import * as subscriptionPlanService from "@/service/subscriptionPlanService";

  @Component
  export default class Dashboard extends BaseComponent {
    
    isLoadingOverviewData = false;
    overviewData = null;
    activeCustomerSubscription = null;
    activeSubscriptionPlan = null;

    overviewItems = [
      { text: this.$t("totalCustomers").toString(), key: "totalCustomers", value: "--", bgColor: "#D7F0F7" },
      { text: this.$t("totalPackages").toString(), key: "totalPackages", value: "--", bgColor: "#D7F0F7" },
      { text: this.$t("totalMeetings").toString(), key: "totalMeetings", value: "--", bgColor: "#D7F0F7" },
      { text: this.$t("totalCustomerAccounts").toString(), key: "totalCustomerAccounts", value: "--", bgColor: "#D7F0F7" },
      { text: this.$t("totalInprogressMeetings").toString(), key: "totalInprogressMeetings", value: "--", bgColor: "#F0FBCD" },
      { text: this.$t("totalPendingMeetings").toString(), key: "totalPendingMeetings", value: "--", bgColor: "#81FF81" },
      { text: this.$t("totalEndedMeetings").toString(), key: "totalEndedMeetings", value: "--", bgColor: "#C3C3C3" },
      { text: this.$t("totalCancelledMeetings").toString(), key: "totalCancelledMeetings", value: "--", bgColor: "#FEAEC9" },
    ];

    isLoadingMeetingParticipantsCountData = false;
    meetingParticipantsCountData = null;

    chartOptions = {
      legend: {
        top: 36,
      },
      tooltip: {
        trigger: 'axis'
      },
      xAxis: {
        data: [],
      },
      yAxis: [
        {
          type: 'value',
          name: this.$t("meeting").toString(),
          position: 'left',
          nameTextStyle: {
            fontWeight: "bold"
          }
        },
        {
          type: 'value',
          name: this.$t("meetingParticipant").toString(),
          position: 'right',
          nameTextStyle: {
            fontWeight: "bold"
          }
        },
      ],
      grid: {
        top: 100,
      },
      series: [
        {
          name: this.$t("meeting").toString(),
          type: 'bar',
          yAxisIndex: 0,
          data: []
        },
        {
          name: this.$t("meetingParticipant").toString(),
          type: 'bar',
          yAxisIndex: 1,
          data: []
        },
        {
          name: this.$t("numCameraOn").toString(),
          type: 'line',
          data: []
        },
      ]
    };
    chartFilterStartDate: Date = null;
    chartFilterEndDate: Date = null;
    chartFilterFormErrorMsg = "";

    todayText = "";
    readonly meetingStatusText = {
      PENDING : this.$t("meetingStatusPending").toString(),
      INPROGRESS : this.$t("meetingStatusInProgress").toString(),
      END : this.$t("meetingStatusEnded").toString(),
      CANCEL : this.$t("meetingStatusCancelled").toString(),
    };
    readonly meetingStatusBadgeVariant = {
      PENDING : "warning",
      INPROGRESS : "success",
      END : "secondary",
      CANCEL : "danger"
    };
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "ID"
      },
      {
        key: "name",
        label: this.$t("meetingName").toString()
      },
      {
        key: "datetime",
        label: this.$t("datetime").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "numUsers",
        label: this.$t("meetingParticipant").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "numVoiceJoined",
        label: this.$t("numVoiceOn").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "numVideos",
        label: this.$t("numCameraOn").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "status",
        label: this.$t("status").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
    ];
    isLoadingMeetingsToday = false;
    meetingsToday = null;


    mounted() {
      this.chartFilterStartDate = moment().subtract(30, "days").startOf("day").toDate();
      this.chartFilterEndDate = moment().endOf("day").toDate();

      this.fetchOverview();
      this.fetchMeetingParticipantsCountData();
      this.fetchMeetingsToday();
      
      if (this.isCustomer()) {
        this.overviewItems.shift();
        this.overviewItems.shift();
      }
      
    }

    @Watch("overviewData", { immediate: true, deep: true })
    onOverviewDataChange(overviewData) {
      for (const overviewItem of this.overviewItems) {
        overviewItem.value = overviewData?.[overviewItem.key] || "--";
      }
    }

    @Watch("meetingParticipantsCountData", { immediate: true, deep: true })
    onMeetingParticipantsCountDataChange(meetingParticipantsCountData) {
      if (meetingParticipantsCountData) {
        this.chartOptions.xAxis.data = meetingParticipantsCountData.map(it => it.scheduleDate);
        this.chartOptions.series[0].data = meetingParticipantsCountData.map(it => it.totalMeetings);
        this.chartOptions.series[1].data = meetingParticipantsCountData.map(it => it.totalParticipants);
        this.chartOptions.series[2].data = meetingParticipantsCountData.map(it => it.totalCameras);
      }
    }

    async fetchOverview() {
      try {
        this.isLoadingOverviewData = true;
        this.overviewData = await reportService.getDashboardOverview();
        if(this.isCustomer())
        {const customerSubscriptions = await subscriptionPlanService.getCustomerSubscription(this.user.customerId);
        this.activeCustomerSubscription = customerSubscriptions.find(it => it.isActive);

        if (this.activeCustomerSubscription) {
          const { subscriptionPlanId } = this.activeCustomerSubscription;
          this.activeSubscriptionPlan = await subscriptionPlanService.getPackageDetail(subscriptionPlanId);
        }}
      } catch (e) {
        this.toastError(e);

      } finally {
        this.isLoadingOverviewData = false;
      }
    }

    async fetchMeetingParticipantsCountData() {
      this.chartFilterFormErrorMsg = "";

      if (this.chartFilterStartDate.getTime() > this.chartFilterEndDate.getTime()) {
        this.chartFilterFormErrorMsg = this.$t("invalidDateRange").toString();
        return;
      }

      const fromDate = this.chartFilterStartDate.toISOString();
      const toDate = this.chartFilterEndDate.toISOString();

      try {
        this.isLoadingMeetingParticipantsCountData = true;
        this.meetingParticipantsCountData = await reportService.getMeetingParticipants(fromDate, toDate);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isLoadingMeetingParticipantsCountData = false;
      }
    }

    async fetchMeetingsToday() {
      try {
        this.isLoadingMeetingsToday = true;
        this.todayText = moment().format("DD/MM/YYYY");
        this.meetingsToday = await reportService.getMeetingListToday();

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isLoadingMeetingsToday = false;
      }
    }


    onClickReloadBtn() {
      this.fetchOverview();
      this.fetchMeetingParticipantsCountData();
      this.fetchMeetingsToday();
    }

    onClickApplyChartDates() {
      this.fetchMeetingParticipantsCountData();
    }


    isBBB(meeting): boolean {
      return meeting?.corePlatform?.toLowerCase() === "bbb";
    }

    isZoom(meeting): boolean {
      return meeting?.corePlatform?.toLowerCase() === "zoom";
    }

    getMeetingChatSettingsTooltipText(meeting) {
      return [
        meeting.enablePrivateChat ? this.$t("meetingPrivateChatAllow").toString() : undefined,
        meeting.enablePublicChat ? this.$t("meetingPublicChatAllow").toString() : undefined,
      ].filter(_.identity).join("\n");
    }

  }
</script>
